import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent, ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../services/user.service';
import {
  CORRECTION_STATUS_SUCCESS,
  CorrectionLineResult,
  PayrollCorrectionService,
} from '../../../../services/payroll-correction.service';

/**
 * Cheques that were voided and unapplied in NAV. Voiding one here marks all its timesheet and
 * invoice lines as reversed and gives the authorization back.
 */
@Component({
  selector: 'ppl-cheque-void',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ListTableComponent,
    PopupDialogComponent,
    NgZorroModule,
    NzFormDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './cheque-void.component.html',
  styleUrl: './cheque-void.component.css'
})
export class ChequeVoidComponent implements OnInit {
  pageId: string = PAGE_CONSTANTS.STAFF_CHEQUE_VOID;
  private readonly VOID_BUTTON_ID = 'Void';
  private readonly PAGE_NAME = 'payrollChequeVoidList';
  readonly REASON_MAX_LENGTH = 1000;

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  programCode = '';
  cheques: any[] = [];
  isReadOnly = true;
  showVoidPopup = false;
  filterForm!: FormGroup;
  voidForm!: FormGroup;
  selectedCheque: any = null;
  hasSearched = false;
  showRequiredError = false;
  private appliedFilters: any[] = [];

  cols = [
    { field: 'chequeNumber', header: 'Check #', jsonpath: 'chequeNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'chequeAmount', header: 'Check \nAmount', jsonpath: 'chequeAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'providerId', header: 'Provider \nID', jsonpath: 'providerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'paidDate', header: 'Paid \nDate', jsonpath: 'paidDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'paidLineCount', header: 'Paid \nLines', jsonpath: 'paidLineCount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'statusLkp', header: 'Status', jsonpath: 'statusLkp', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'modifiedDate', header: 'Modified \nDate', jsonpath: 'modifiedDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'modifiedByName', header: 'Modified \nBy', jsonpath: 'modifiedByName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  /** Sub-row columns for a cheque's lines; set from the first lines loaded. */
  lineItemCols: any[] = [];

  buttons = [
    {
      button_id: this.VOID_BUTTON_ID,
      button_label: 'Void Check',
      buttonAction: 'voidCheque',
      className: 'primary-btn-color'
    },
  ];

  constructor(
    private fb: FormBuilder,
    private payrollCorrectionService: PayrollCorrectionService,
    private notificationService: NotificationService,
    private errorAlertService: ErrorAlertService,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    this.programCode = this.userService.getSelectedProgram()?.program_code ?? '';
    this.isReadOnly = !this.allowListStore.isButtonAllowed(this.pageId, this.VOID_BUTTON_ID);
    this.filterForm = this.fb.group({
      providerPplId: [null],
      chequeNumber: [null],
    });
    this.voidForm = this.fb.group({
      reasonCode: [null, [Validators.required, Validators.pattern(/\S/), Validators.maxLength(this.REASON_MAX_LENGTH)]],
    });
  }

  /** Expand/collapse a cheque row, loading the lines it paid the first time it is expanded. */
  rowClicked(row: any) {
    // The grid emits sub-row cell clicks through the same event; lines don't expand.
    if (row.isChequeLine) return;
    if (row.linesLoaded) {
      row.showLineItem = !row.showLineItem;
      return;
    }

    this.payrollCorrectionService.getChequeLines(this.programCode, row.chequeNumber, row.providerId)
      .subscribe((data: any) => {
        // ppl-list-table renders a column named processingStatus as a hyperlink.
        const lines = (data?.responsedata ?? []).map((line: any) =>
          ({ ...line, processingStatusText: line.processingStatus, isChequeLine: true }));
        if (!this.lineItemCols.length && lines.length) {
          this.lineItemCols = this.buildLineColumns(lines[0]);
        }
        row.lineItems = lines;
        row.showLineItem = true;
        row.linesLoaded = true;
      });
  }

  // TODO: replace with fixed columns once the cheque-lines response fields are confirmed.
  private buildLineColumns(line: any): any[] {
    return Object.keys(line)
      .filter(key => !['processingStatus', 'isChequeLine'].includes(key))
      .map(key => ({
        field: key,
        header: key === 'processingStatusText' ? 'Processing Status' : this.toHeader(key),
        jsonpath: key,
        isDisplayedAtHeader: 'y',
        isSearchable: 'n',
      }));
  }

  /** e.g. lineId -> "Line Id", serviceCodeRate -> "Service Code Rate". */
  private toHeader(key: string): string {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  /** Nothing is loaded until the user searches with at least one filter; the full list grows too large. */
  onSearch() {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }
    const { providerPplId, chequeNumber } = this.filterForm.value;
    const filters = [];
    if (providerPplId?.trim()) {
      filters.push({ jsonPath: 'providerId', value: providerPplId.trim(), operator: 'Equal' });
    }
    if (chequeNumber?.trim()) {
      filters.push({ jsonPath: 'chequeNumber', value: chequeNumber.trim(), operator: 'Equal' });
    }
    this.showRequiredError = filters.length === 0;
    if (this.showRequiredError) return;

    this.appliedFilters = filters;
    this.hasSearched = true;
    this.reload();
  }

  onClear() {
    this.filterForm.reset();
    this.showRequiredError = false;
    this.appliedFilters = [];
    this.hasSearched = false;
    this.cheques = [];
  }

  onPageLoad(event: any) {
    this.loadCheques(event.startingPageNumber, event.rows ?? 20);
  }

  loadCheques(startingPageNumber = 1, recordsPerPage = 20) {
    const payload = {
      pageName: this.PAGE_NAME,
      programCode: this.programCode,
      condition: 'and',
      recordsPerPage,
      startingPageNumber,
      searchParam: this.appliedFilters,
      orderByColumn: ['providerId'],
      orderBy: ['desc'],
    };

    this.payrollCorrectionService.getListPageData(payload).subscribe((data: any) => {
      const records = data?.responsedata ?? [];
      this.cheques = startingPageNumber > 1 ? [...this.cheques, ...records] : records;
    });
  }

  gridAction(event: any) {
    if (event.button.buttonAction !== 'voidCheque') return;
    const selected: any[] = event.selectedCheckBox ?? [];
    // The void API works on one cheque (cheque number + provider) per call.
    if (selected.length !== 1) {
      this.errorAlertService.openModal('error', 'Error', 'Please select one cheque to void.');
      return;
    }
    this.selectedCheque = selected[0];
    this.voidForm.reset();
    this.showVoidPopup = true;
  }

  submitVoid() {
    if (this.voidForm.invalid) {
      this.voidForm.markAllAsTouched();
      return;
    }

    const { chequeNumber, providerId } = this.selectedCheque;
    const reasonCode = this.voidForm.value.reasonCode.trim();
    this.payrollCorrectionService.chequeVoid(this.programCode, chequeNumber, providerId, reasonCode)
      .subscribe((data: any) => {
        this.showVoidPopup = false;
        this.notifyResults(chequeNumber, data?.responsedata ?? []);
        this.reload();
      });
  }

  cancelVoid() {
    this.showVoidPopup = false;
  }

  private notifyResults(chequeNumber: string, results: CorrectionLineResult[]) {
    const succeeded = results.filter(r => r.status === CORRECTION_STATUS_SUCCESS).length;
    const failed = results.length - succeeded;
    if (failed === 0) {
      this.notificationService.success('Success', `Cheque ${chequeNumber} voided. ${succeeded} line(s) reversed.`);
    } else if (succeeded === 0) {
      this.notificationService.error('Error', `Cheque ${chequeNumber} could not be voided. ${failed} line(s) were not reversed.`);
    } else {
      this.notificationService.alert('warning', 'Partially Voided',
        `Cheque ${chequeNumber}: ${succeeded} line(s) reversed, ${failed} could not be reversed.`);
    }
  }

  /** Clear the grid's selection so a voided cheque isn't sent again, then reload from page 1. */
  private reload() {
    this.selectedCheque = null;
    if (this.childComponent?.selectedCheckBoxValue) {
      this.childComponent.selectedCheckBoxValue = [];
    }
    this.loadCheques(1, this.childComponent?.recordsPerPage ?? 20);
  }
}
